import { useState } from 'react';
import {
  type AdminOnboardingFunnel,
  type AdminOnboardingGroup,
  type AdminOnboardingSummary,
  type AdminOnboardingTally,
  type ClientEventSource,
  type OnboardingStep,
  onboardingStepsOf,
} from '@boardflight/shared';
import { api } from '../api.js';
import { Async, Panel, duration, useAsync } from '../ui.js';

/**
 * The onboarding: every place it runs added up first, and any one of them on request
 * (`docs/off-board-plan.md` §5.6).
 *
 * Three groups: the desktop app, the website, and the Board app that was retired. A row in
 * `client_events` says which shell wrote it, or says nothing — and "nothing" is the old Board app,
 * kept as it was and labelled as what it is.
 *
 * **All of them is the default** (owner, 2026-10-28): the question is how many people get through,
 * and the place is a filter on it. The groups walk different paths, so the total is drawn only on
 * the outcomes every path shares — opened, signed in, connected a Board, reached the library — and
 * picking one group draws its own path in full (`onboardingStepsOf`), so a step a group never had
 * is not drawn as a drop to zero.
 *
 * Every number is installs, and every stage is out of the installs whose onboarding *started* in
 * the range. Indented rows are a breakdown of the row above them rather than a further stage.
 */
const GROUPS: Record<ClientEventSource, { label: string; unit: string; about: string }> = {
  desktop: {
    label: 'Desktop app',
    unit: 'desktop installs',
    about: 'An install is the app window’s own id, so reinstalling the app starts again.',
  },
  web: {
    label: 'Website',
    unit: 'browsers',
    about: 'An install is one browser profile, so a second browser or cleared site data starts again.',
  },
  board: {
    label: 'Board app (retired)',
    unit: 'Boards',
    about: 'Everything the Board app wrote before it was retired.',
  },
};

type Chosen = ClientEventSource | 'all';

/** Every group's funnel added up, field by field: each is a count of installs, and no install is in two groups. */
export function summed(groups: readonly AdminOnboardingGroup[]): AdminOnboardingFunnel {
  const total = {} as Record<keyof AdminOnboardingFunnel, number>;
  for (const group of groups) {
    for (const [key, value] of Object.entries(group.funnel) as [keyof AdminOnboardingFunnel, number][]) {
      total[key] = (total[key] ?? 0) + value;
    }
  }
  return total as AdminOnboardingFunnel;
}

export function Onboarding() {
  const [days, setDays] = useState(30);
  const [chosen, setChosen] = useState<Chosen>('all');
  const state = useAsync(
    () => api<AdminOnboardingSummary>(`/v1/admin/onboarding?days=${days}`),
    [days],
  );
  const pick = (data: AdminOnboardingSummary): AdminOnboardingGroup | undefined =>
    chosen === 'all' ? undefined : data.groups.find((group) => group.source === chosen);

  return (
    <>
      <Panel
        title="Onboarding"
        actions={
          <div className="filters">
            <label className="filter">
              <span className="muted small">Started</span>
              <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Range">
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
              </select>
            </label>
            <label className="filter">
              <span className="muted small">Where</span>
              <select value={chosen} onChange={(e) => setChosen(e.target.value as Chosen)} aria-label="Where the onboarding ran">
                <option value="all">Everywhere</option>
                {(['desktop', 'web', 'board'] as const).map((source) => (
                  <option key={source} value={source}>{GROUPS[source].label}</option>
                ))}
              </select>
            </label>
          </div>
        }
      >
        <Async state={state}>
          {(data) => {
            const group = pick(data);
            return group ? <Funnel group={group} /> : <AllFunnel groups={data.groups} onPick={setChosen} />;
          }}
        </Async>
      </Panel>

      <Panel title="What the piece step was answered with">
        <Async state={state}>
          {(data) => {
            const group = pick(data);
            const groups = group ? [group] : data.groups;
            const answered = groups.reduce((sum, one) => sum + one.funnel.piecesConfirmed, 0);
            const sets = new Map<string, AdminOnboardingGroup['sets'][number]>();
            for (const one of groups) {
              for (const set of one.sets) {
                const seen = sets.get(set.key);
                sets.set(set.key, seen ? { ...seen, boards: seen.boards + set.boards } : set);
              }
            }
            const rows = [...sets.values()].sort((a, b) => b.boards - a.boards);
            const share = (n: number): string =>
              answered === 0 ? '—' : `${Math.round((n / answered) * 100)}%`;
            if (rows.length === 0) return <p className="note">Nobody answered it in this range.</p>;
            return (
              <>
                <p className="note">
                  Each install's latest answer, of the {answered} that answered. Board Arcade and Chop Chop
                  start ticked.
                </p>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Set</th>
                        <th className="num">Installs</th>
                        <th className="num">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((set) => (
                        <tr key={set.key}>
                          <td>
                            {set.display}
                            {set.known ? null : (
                              <span className="pill" style={{ marginLeft: 6 }}>newer client</span>
                            )}
                          </td>
                          <td className="num">{set.boards}</td>
                          <td className="num">{share(set.boards)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          }}
        </Async>
      </Panel>

      <Panel title="By release">
        <Async state={state}>
          {(data) => {
            const group = pick(data);
            const rows = (group ? [group] : data.groups).flatMap((one) =>
              one.versions.map((row) => ({ ...row, source: one.source })),
            );
            if (rows.length === 0) return <p className="note">Nothing started in this range.</p>;
            return (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Release</th>
                      {group ? null : <th>Where</th>}
                      <th className="num">Started</th>
                      <th className="num">Reached the library</th>
                      <th className="num">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.source}:${row.clientVersion ?? 'unknown'}`}>
                        <td className="mono">{row.clientVersion ?? 'unknown'}</td>
                        {group ? null : <td className="muted">{GROUPS[row.source].label}</td>}
                        <td className="num">{row.started}</td>
                        <td className="num">{row.completed}</td>
                        <td className="num">{percent(row.completed, row.started)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }}
        </Async>
      </Panel>
    </>
  );
}

/**
 * Every group at once, on the outcomes every path shares. A Board is connected by pairing it in the
 * app, or — on the website — by a desktop app connecting one while the page waited.
 */
function AllFunnel({ groups, onPick }: { groups: readonly AdminOnboardingGroup[]; onPick: (source: Chosen) => void }) {
  const f = summed(groups);
  const rows: Row[] = [
    { label: 'Opened the onboarding', value: f.started },
    { label: 'Signed in', value: f.linked },
    { label: 'with a new account', value: f.linkedNewAccount, of: f.linked, indent: true },
    { label: 'Connected a Board', value: f.paired + f.handoffLinked },
    { label: 'Answered the piece step', value: f.piecesConfirmed },
    { label: 'Reached the library', value: f.completed },
    { label: 'Left without finishing', value: f.exited },
  ];
  return (
    <>
      <FunnelTable rows={rows} started={f.started} />
      <p className="note">
        {groups
          .filter((group) => group.funnel.started > 0)
          .map((group, index) => (
            <span key={group.source}>
              {index > 0 ? ' · ' : ''}
              <button className="link" onClick={() => onPick(group.source)}>{GROUPS[group.source].label}</button>{' '}
              {group.funnel.started} started, {group.funnel.completed} finished
            </span>
          ))}
      </p>
    </>
  );
}

interface Row {
  label: string;
  value: number;
  /** What the share is out of. Left out: the installs that started. */
  of?: number;
  indent?: boolean;
  note?: string;
}

const tallied = (tallies: AdminOnboardingTally[], words: Record<string, string>): string =>
  tallies.map((tally) => `${words[tally.key] ?? tally.key} ${tally.installs}`).join(' · ');

const FOUND_BY: Record<string, string> = {
  mdns: 'announced on the network',
  sweep: 'network sweep',
  remembered: 'remembered address',
  typed: 'typed address',
};

const PAIR_FAILURES: Record<string, string> = {
  refused: 'the Board refused',
  timeout: 'nobody answered in time',
  unreachable: 'the Board could not be reached',
  error: 'something else',
};

/**
 * The rows for one step of one group's path — the step reached, then what happened on it.
 *
 * Written per step rather than per group, so the three funnels cannot drift apart on what "paired"
 * or "signed in" means: a step two groups share is the same rows in both.
 */
function stepRows(step: OnboardingStep, group: AdminOnboardingGroup): Row[] {
  const f: AdminOnboardingFunnel = group.funnel;
  const desktop = group.source === 'desktop';
  switch (step) {
    case 'welcome':
      return [
        { label: 'Opened the onboarding', value: f.started },
        ...(desktop
          ? [{ label: 'for the first time on that computer', value: f.appOpened, of: f.started, indent: true }]
          : []),
      ];
    case 'account':
      return [
        { label: 'Reached the account step', value: f.account },
        // A code is the Board app's and the desktop app's way in; the website has its own form.
        ...(group.source === 'web'
          ? []
          : [
              { label: 'Saw a sign-in code', value: f.codeShown, of: f.account, indent: true },
              { label: 'Had a code fail', value: f.failed, of: f.account, indent: true },
            ]),
        { label: 'Signed in', value: f.linked },
        {
          label: 'with a new account',
          value: f.linkedNewAccount,
          of: f.linked,
          indent: true,
          note: 'Created after the onboarding started.',
        },
        { label: 'Skipped signing in', value: f.skipped },
      ];
    case 'get_app':
      return [
        { label: 'Reached “Get the app”', value: f.getApp },
        { label: 'Pressed the download', value: f.appDownloadClicked, of: f.getApp, indent: true },
      ];
    case 'waiting':
      return [
        { label: 'Reached “Waiting for your Board”', value: f.waiting },
        {
          label: 'A Board appeared on the account',
          value: f.handoffLinked,
          note: 'Connected by a desktop app while the page was watching.',
        },
      ];
    case 'find':
      return [
        { label: 'Reached “Find your Board”', value: f.find },
        {
          label: 'Found a Board',
          value: f.boardFound,
          note: group.foundBy.length > 0 ? tallied(group.foundBy, FOUND_BY) : undefined,
        },
        { label: 'Had a search find nothing', value: f.boardNotFound, of: f.find, indent: true },
      ];
    case 'pair':
      return [
        { label: 'Reached the pair step', value: f.pair },
        { label: 'Asked the Board to pair', value: f.pairRequested, of: f.pair, indent: true },
        { label: 'Paired the Board', value: f.paired },
        {
          label: 'Had a pairing fail',
          value: f.pairFailed,
          of: f.pair,
          indent: true,
          note: group.pairFailures.length > 0 ? tallied(group.pairFailures, PAIR_FAILURES) : undefined,
        },
        { label: 'Skipped pairing', value: f.pairSkipped },
      ];
    case 'pieces':
      return [
        { label: 'Reached the piece step', value: f.pieces },
        { label: 'Answered it', value: f.piecesConfirmed },
        { label: 'with exactly the defaults', value: f.piecesDefaultsKept, of: f.piecesConfirmed, indent: true },
      ];
    case 'background':
      return [
        { label: 'Reached “Keep BoardFlight running”', value: f.background },
        { label: 'Answered it', value: f.backgroundChosen },
        { label: 'with yes', value: f.backgroundEnabled, of: f.backgroundChosen, indent: true, note: 'Their latest answer.' },
      ];
    case 'notifications':
      // What the system was told is the phone's own business: the step is counted, not its answer.
      return [{ label: 'Reached the notifications step', value: f.notifications }];
    case 'done':
      return [
        { label: 'Reached the last step', value: f.done },
        { label: 'Reached the library', value: f.completed },
        { label: 'Left without finishing', value: f.exited },
      ];
  }
}

function Funnel({ group }: { group: AdminOnboardingGroup }) {
  const f = group.funnel;
  const about = GROUPS[group.source];
  const rows = onboardingStepsOf(group.source).flatMap((step) => stepRows(step, group));

  return (
    <>
      <p className="note">
        <strong>{f.started}</strong> {about.unit} started, <strong>{f.completed}</strong> reached the library
        {group.medianSeconds === null ? '.' : `, in a median of ${duration(group.medianSeconds * 1000)}.`}{' '}
        {about.about}
      </p>
      <FunnelTable rows={rows} started={f.started} />
    </>
  );
}

function FunnelTable({ rows, started }: { rows: Row[]; started: number }) {
  return (
    <>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Stage</th>
              <th className="num">Installs</th>
              <th className="num">Of</th>
              <th style={{ width: '40%' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const of = row.of ?? started;
              return (
                <tr key={row.label}>
                  <td style={row.indent ? { paddingLeft: 32 } : undefined}>
                    {row.indent ? <span className="muted">↳ </span> : null}
                    {row.label}
                    {row.note ? <div className="note">{row.note}</div> : null}
                  </td>
                  <td className="num">{row.value}</td>
                  <td className="num muted">{percent(row.value, of)}</td>
                  <td>
                    <div
                      style={{
                        height: 8,
                        marginTop: 7,
                        borderRadius: 4,
                        background: 'var(--line)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: of === 0 ? 0 : `${Math.min(100, (row.value / of) * 100)}%`,
                          background: row.indent ? 'var(--muted)' : 'var(--accent)',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function percent(n: number, of: number): string {
  return of === 0 ? '—' : `${Math.round((n / of) * 100)}%`;
}
